/**
 * Assembles the cleanroom, its tools, and the picking that lets a student walk
 * up to a tool and open it.
 *
 * The etcher gets one extra thing no other tool has: a cutaway chamber with a
 * real wafer in it, textured from the live simulation. Walking up to ETCH-01
 * and seeing the thickness map change while the recipe runs is the moment the
 * two halves of the project - the 3D fab and the solver stack - visibly become
 * one thing.
 */

import * as THREE from 'three'
import { aisleDirection, BULKHEAD_Z, FAB, GOWNING, LITHO_LIGHT_COLOUR, SECOND_ROW_PIVOT_Z, TOOLS, type Tool,
  toolFaceZ,
  toolSize,
} from './layout'
import {
  buildAmrs,
  hmiFaceX,
  buildAttachments,
  buildGowning,
  ENV_PROBE,
  buildLighting,
  buildOht,
  buildStockers,
  KEY_OFFSET,
  newAttachments,
  partitionCollider,
  partitions,
  stockerColliders,
  type Amrs,
  type AttachmentsGroup,
  type Stockers,
  type TowerState,
  type VehicleRef,
  type VehicleStatus,
  buildRoom,
  buildSecondRow,
  buildTool,
  buildToolTrigger,
  createMaterials,
  type FabMaterials,
  type LightingRig,
  type Oht,
} from './builders'
import { buildBays, MAINTENANCE_TOOL, SIDE_REACH, type Bays } from './bays'
import { buildChase, type Chase } from './chase'
import type { ChaseCrew } from './chaseCrew'
import { BALLROOM } from './hall'
import type { HmiReadout } from './hmi'
import type { SoundSources } from './sound'
import { lightingPreset } from './lighting'
import type { People } from './people'
import type { SubfabCrew } from './subfabCrew'
import type { QualityTier } from './quality'
import type { Collider } from './Player'
import { buildSubfab, buildSubfabLamps, type Subfab } from './subfab'
import { BOOT_TIMING } from './bootTiming'
import type { HmiScreens } from './hmi'
import { SUBFAB, subfabColliders } from './subfabLayout'
import type { HouseState, HouseSystem } from '../sim/house/model'
import { buildWipStand, type WipStand } from './wipStand'

/**
 * How far a student can reach a tool from. Generous enough to click a tool
 * from the middle of the aisle, short enough that you cannot operate the far
 * end of the fab from the gowning door.
 */
const INTERACT_RANGE = 11

export interface PickResult {
  tool: Tool
  distance: number
}

/**
 * How far a bay downlight reaches, in metres: its 15 m falloff and a step.
 * No longer a live cull (see `lightBudget` for why culling by radius was a
 * bug); it survives as the ruler the per-tier lamp budget is measured with,
 * so the budget still means "every lamp that could touch the image". It was
 * 22 for a while - "a little past" the falloff - and the difference was
 * lamps in the budget that lit nothing: a PointLight with a `distance` is
 * exactly zero beyond it, and every lamp in the budget is shaded for every
 * pixel on screen. On the integrated GPU that margin was a third of the
 * lean tier's lighting cost for no light.
 */
const LIGHT_CULL_RADIUS = 15.5

/** The key light's shadow box, as `buildLighting` sizes it. */
const SHADOW_HALF_EXTENT = 26
const SHADOW_FAR = 60

/**
 * What the environment capture is allowed to see.
 *
 * The capture is the scene's FIRST render, and a render compiles a shader
 * program for every material it meets - under the capture's own light state
 * and with `environment` still null, which gives those programs cache keys
 * the prewarm's tiers never match. Measured on the integrated GPU: 2.7 s of
 * the boot compiling programs that are used for six faces and never again,
 * against 20 ms for the cube render itself once nothing is left to compile.
 *
 * So the cube camera looks at one layer, and only the room shell, its
 * luminaire rows and the lighting rig are on it. That is what the reflection
 * is for - the ceiling glow in a polished floor - and it costs a handful of
 * programs. What it gives up is the smeared silhouette of a tool in the floor
 * under it, which at a 512 cube from one probe was never more than a blur.
 */
const ENV_LAYER = 1

export class FabScene {
  private rig!: LightingRig
  private lamps: THREE.PointLight[] = []
  /**
   * The sub-fab, once built. Null until then: the room is built behind the
   * first frame ('ensureSubfab'), because a student who spawns on the hall
   * floor cannot see it and should not wait for it. Its LAMPS are built at
   * construction regardless - see buildSubfabLamps for why.
   */
  private subfab: Subfab | null = null
  private subfabLamps: THREE.PointLight[] = []
  /** A crew that arrived before the room did; added the moment it exists. */
  private pendingCrew: SubfabCrew | null = null
  /** The last house state, replayed onto a room built after it was sent. */
  private lastHouse: [HouseState, string | null] | null = null
  /** The etcher's WIP stand (wipStand.ts), once the floor is built. */
  private wipStand: WipStand | null = null
  /** Whether the floor is currently showing warm cooling water. */
  private pcwWarm = false
  private readonly renderer?: THREE.WebGLRenderer
  private envTarget: THREE.WebGLCubeRenderTarget | null = null
  private built = false
  /**
   * Everything the fab owns, under one node. The scene ROOT belongs to the
   * caller (today a plain THREE.Scene in main; under react-three-fiber, the
   * canvas's own root) - the fab only decorates it: content goes in this
   * group, and the root-level atmosphere (background, fog, environment) is
   * written through the injected root reference, because three.js honours
   * those only on the root Scene.
   */
  readonly group = new THREE.Group()
  readonly materials: FabMaterials

  private readonly triggers: THREE.Mesh[] = []
  private readonly raycaster = new THREE.Raycaster()
  private readonly ray = new THREE.Ray()
  private readonly hmiScreens = new Map<string, THREE.Mesh>()
  /** Each tool's built geometry, for asking 'is the crosshair on the machine?'. */
  private readonly toolGroups = new Map<string, THREE.Group>()

  /** Live wafer surface inside the etch chamber cutaway. */
  private waferMesh: THREE.Mesh | null = null
  private waferTexture: THREE.CanvasTexture | null = null
  private plasmaGlow: THREE.Mesh | null = null
  /** The discharge as last solved, 0..1 - what the plasma shimmer rides on. */
  private plasmaLevel = 0
  private plasmaBase = new THREE.Color(0x8fd0ff)
  private clock = 0

  /**
   * The parts of the hall that turn while a tool runs: platens, lapping
   * plates, saw blades. Tagged by the builders with `userData.spin` in
   * radians per second and driven from `animate`. Nothing else in the room
   * moves, and that is the point - one turning platen among still machinery
   * reads as a working floor, while a room full of motion reads as a toy.
   */
  private readonly spinners: THREE.Object3D[] = []
  /** The overhead hoist loop and the vehicles circulating on it. */
  private oht!: Oht
  /** The mobile robots running the lanes along the tool fronts. */
  private amrs!: Amrs
  private stockers!: Stockers
  /** The bays' wall furniture and andon boards (bays.ts). */
  private bays!: Bays
  /** The service side of the wall: deck, headers, panels (chase.ts). */
  private chase!: Chase
  /** The instanced carriers, EMOs and towers, with the towers' lamps. */
  private attachmentsGroup!: AttachmentsGroup
  /** The technicians, once their model has loaded (see people.ts). */
  private people: People | null = null
  /** The sub-fab crew, once loaded (subfabCrew.ts); lives inside the sub-fab group. */
  private crew: SubfabCrew | null = null

  addSubfabCrew(crew: SubfabCrew): void {
    this.crew = crew
    // Inside the sub-fab's group, so it shares the room's visibility gate -
    // or held until that group exists, when the model beats the room.
    if (this.subfab) this.subfab.group.add(crew.group)
    else this.pendingCrew = crew
  }

  /**
   * Build the sub-fab if it is not built. True when this call built it, so
   * the caller knows to re-prewarm and refresh the walk's colliders.
   *
   * Called synchronously from the composition root when the restored place
   * is below the slab - the student is standing in it - and otherwise from
   * the canvas on the first idle moment after the first frame.
   */
  ensureSubfab(): boolean {
    if (this.subfab) return false
    const subfab = buildSubfab(this.subfabLamps)
    this.subfab = subfab
    this.group.add(subfab.group)
    if (this.pendingCrew) {
      subfab.group.add(this.pendingCrew.group)
      this.pendingCrew = null
    }
    if (this.lastHouse) subfab.update(...this.lastHouse)
    return true
  }

  /** The gowned technicians behind the wall (chaseCrew.ts); inside the chase group. */
  private chaseCrew: ChaseCrew | null = null

  addChaseCrew(crew: ChaseCrew): void {
    this.chaseCrew = crew
    // Inside the chase's group, so it is drawn only when the chase is.
    this.chase.group.add(crew.group)
  }

  /** Put the loaded technicians in the hall. */
  addPeople(people: People): void {
    this.people = people
    this.group.add(people.group)
    // Reachable from the console in development, the way the sound graph
    // is (FabCanvas): the figures are the one part of the scene whose
    // timing has to be judged against a clock - a standing person's
    // breathing rate, a walk cycle's stride - and that is unreadable from
    // a screenshot.
    if (import.meta.env.DEV) (window as unknown as Record<string, unknown>).__simfabPeople = people
  }

  /**
   * What outlives a hall: the loaded models and the sub-fab, which is the
   * same room under both layouts. Taken out of this scene's groups so
   * `dispose` will not tear them down, and handed to the next scene.
   */
  detach(): { people: People | null; subfabCrew: SubfabCrew | null; chaseCrew: ChaseCrew | null; subfab: Subfab | null } {
    const out = { people: this.people, subfabCrew: this.crew ?? this.pendingCrew, chaseCrew: this.chaseCrew, subfab: this.subfab }
    this.people?.group.removeFromParent()
    this.crew?.group.removeFromParent()
    this.chaseCrew?.group.removeFromParent()
    this.subfab?.group.removeFromParent()
    this.people = null
    this.crew = null
    this.pendingCrew = null
    this.chaseCrew = null
    this.subfab = null
    return out
  }

  /**
   * A sub-fab built by the previous hall. Its geometry is layout-free; its
   * lamps were that hall's and went with it, and this hall made its own at
   * construction, so only the room is taken.
   */
  adoptSubfab(subfab: Subfab): void {
    if (this.subfab) return
    this.subfab = subfab
    this.group.add(subfab.group)
    if (this.pendingCrew) {
      subfab.group.add(this.pendingCrew.group)
      this.pendingCrew = null
    }
    if (this.lastHouse) subfab.update(...this.lastHouse)
  }

  /**
   * Tear the hall down: out of the scene, and every geometry, material and
   * texture it made released. Call `detach` first, and build the NEXT hall
   * before this - its materials hold the same shader programs, so releasing
   * these does not release those. The environment target goes too; the next
   * hall has assigned its own by now.
   */
  /**
   * Out of the scene, nothing released. Called BEFORE the next hall is built,
   * and the reason is measured: with this hall still in the root, the next
   * hall's environment capture and prewarm see both rigs' lamps - roughly
   * twice the point-light count - and compile a whole set of shader
   * variants under it. Compile time explodes with light count, and the
   * variants are useless the moment this hall goes. That was 32 s in the
   * capture alone. Unmounted first, the next hall sees only its own lights.
   */
  unmount(): void {
    this.root.remove(this.group)
  }

  dispose(): void {
    this.unmount()
    const materials = new Set<THREE.Material>()
    this.group.traverse((o) => {
      const mesh = o as THREE.Mesh
      if (!mesh.isMesh) return
      mesh.geometry?.dispose()
      const m = mesh.material
      if (Array.isArray(m)) m.forEach((x) => materials.add(x))
      else if (m) materials.add(m)
    })
    for (const m of Object.values(this.materials)) {
      if ((m as THREE.Material | undefined)?.isMaterial) materials.add(m as THREE.Material)
    }
    for (const m of materials) {
      for (const v of Object.values(m as unknown as Record<string, unknown>)) {
        if ((v as THREE.Texture | null)?.isTexture) (v as THREE.Texture).dispose()
      }
      m.dispose()
    }
    this.envTarget?.dispose()
    this.envTarget = null
  }

  constructor(
    private readonly root: THREE.Scene,
    screens: HmiScreens,
    renderer?: THREE.WebGLRenderer,
  ) {
    this.renderer = renderer
    // Background and fog have to sit in the same key as the walls - a dark fog
    // against bright walls reads as smoke, which is the one thing a cleanroom
    // does not have. Both are painted by `applyLighting` below, so the haze
    // follows the preset: high-key in the bright fab, cold in the blue one.
    //
    // THE RANGE IS SHORT BECAUSE THE SIGHTLINES ARE. 34/96 was set when the
    // aisle was 10 m wide and ran 136 m without an interruption, and hiding
    // the far end of that tunnel was a job worth doing. It is not that room
    // any more: the hall is 5.4 m across with a bay divider every few
    // metres, so almost everything on screen is inside 30 m and the fog
    // never engaged at all - dropped in with a scarlet fog colour to prove
    // it, only a slot a few degrees wide at the vanishing point took any.
    //
    // 40/200, and THE FOG IS NOT WHAT MAKES THE FAR HALF GO FLAT. That was
    // worth testing rather than assuming, and the scarlet trick above still
    // answers it in one frame: paint the fog red, stand at one end of the
    // hall, and the red is a smudge a few dozen pixels across at the
    // vanishing point. Everything else on screen is inside 30 m, because the
    // aisle is 5.4 m wide with a bay divider every few metres and the tool
    // rows close the view long before the air does. What flattens the far
    // half is that everything in it is small and was, until the floor's
    // albedo came down, all the same value - not haze.
    //
    // What the range does own is that one slot. At 24/110 anything past 110 m
    // was clamped to solid fog colour, so on the single end-to-end sightline -
    // 136 m of it - the last stretch was a grey plug. 40/200 puts the far wall
    // at about 60 per cent instead: still a clear ramp, still says the room is
    // long, no longer a wall of nothing. It stays deliberately weak either
    // way: a cleanroom is the one room on earth engineered to have nothing in
    // the air, so this is a depth cue for the far slot, not atmosphere, and
    // anything stronger is a lie about what the room is for.
    this.root.fog = new THREE.Fog(0xdde3e8, 40, 200)
    // Where the build's time goes, for the boot report in main.tsx (dev only).
    const mark = (stage: string): void => {
      if (BOOT_TIMING) performance.mark('boot:fab:' + stage)
    }
    this.materials = createMaterials()
    mark('materials')

    const room = buildRoom(this.materials)
    room.traverse((o) => o.layers.enable(ENV_LAYER))
    this.group.add(room)
    this.group.add(buildGowning(this.materials, GOWNING.position))
    this.oht = buildOht(this.materials)
    this.group.add(this.oht.group)
    this.amrs = buildAmrs(this.materials)
    this.group.add(this.amrs.group)
    mark('room+gowning+oht+amr')

    // The sub-fab's lamps only. The room itself comes behind the first frame
    // ('ensureSubfab') - only the stairwell hole lets it show, and nobody
    // spawning on the hall floor is looking down it in the first second -
    // but its lamps join the pool now, before the budget below is scanned.
    this.subfabLamps = buildSubfabLamps()
    mark('subfab lamps')

    this.rig = buildLighting(this.group, TOOLS)
    this.rig.hemi.layers.enable(ENV_LAYER)
    this.rig.key.layers.enable(ENV_LAYER)
    for (const lamp of this.rig.lamps) lamp.layers.enable(ENV_LAYER)
    // The key light's target has to be IN the scene for its matrix to
    // update, and it has to be in the scene because `followEye` moves it.
    this.group.add(this.rig.key.target)
    for (const lamp of this.subfabLamps) {
      this.group.add(lamp)
      this.rig.lamps.push(lamp)
    }
    this.lamps = this.rig.lamps
    this.applyLighting()
    mark('lighting')
    // The lamp budget is taken below, once the chase has added its own:
    // it counts every lamp that can touch the image, so it has to see the
    // last of them.

    const attachments = newAttachments()
    for (const tool of TOOLS) {
      // `tool` is the data; `group` is the geometry built from it.
      const group = buildTool(tool, this.materials, attachments)
      this.toolGroups.set(tool.id, group)
      this.group.add(group)
      group.traverse((child) => {
        if (typeof child.userData.spin === 'number') this.spinners.push(child)
      })

      const trigger = buildToolTrigger(tool)
      this.triggers.push(trigger)
      this.group.add(trigger)

      group.traverse((child) => {
        if (child.userData.isHmiScreen) this.hmiScreens.set(tool.id, child as THREE.Mesh)
      })

      // Bind every screen's texture NOW, before the prewarm compile. A
      // material's first `map` assignment changes its shader program hash,
      // so binding lazily - on the first paint, when a student wandered
      // within reading range - recompiled mid-walk, the same hitch the
      // constant light budget exists to prevent. From here on, painting a
      // screen only flags this texture dirty.
      const screenMesh = this.hmiScreens.get(tool.id)
      if (screenMesh) {
        const tex = new THREE.CanvasTexture(screens.canvasFor(tool.id))
        tex.colorSpace = THREE.SRGBColorSpace
        const mat = screenMesh.material as THREE.MeshBasicMaterial
        mat.map = tex
        mat.color.set(0xffffff)
        mat.needsUpdate = true
      }

      if (tool.id === 'etch') this.buildEtchCutaway(tool)
    }
    mark('tools x22')
    // Fab B's second row, back-to-back with the first across the service
    // aisle. Geometry only - no triggers, no screens, no tool groups - and
    // its carriers, towers and labels file into the same instanced sets.
    if (BALLROOM) this.group.add(buildSecondRow(this.materials, attachments))
    // The stockers file their carriers into the same instanced set.
    this.stockers = buildStockers(this.materials, attachments)
    this.group.add(this.stockers.group)
    // The bays' furniture and buffers - their carriers too.
    this.bays = buildBays(this.materials, attachments)
    this.group.add(this.bays.group)
    mark('stockers+bays')
    // The service side of the same wall. Built after the bays, because the
    // bulkhead is drawn first as the hall's occluder and everything in here
    // is meant to be depth-rejected behind it from the aisle.
    this.chase = buildChase(this.materials, this.bays.openings())
    this.group.add(this.chase.group)
    mark('chase')
    // Its strip lamps join the shared pool, like the sub-fab's, and only
    // now can the per-tier budget be measured: it is the worst-case count
    // of lamps within reach of any walkable spot, and these are the last.
    for (const lamp of this.chase.lamps) {
      this.group.add(lamp)
      this.rig.lamps.push(lamp)
    }
    this.applyLightMix()
    this.computeLightBudgets()
    mark('lightBudgets')
    // The carriers and EMOs of every tool, a few instanced draws for the lot.
    this.attachmentsGroup = buildAttachments(this.materials, attachments)
    this.group.add(this.attachmentsGroup.group)
    mark('attachments')
    for (const tool of TOOLS) {
      this.attachmentsGroup.setTower(tool.id, tool.id === MAINTENANCE_TOOL ? 'down' : 'idle')
    }
    // Where lots wait at the etcher - see wipStand.ts. One staged lot on a
    // healthy line; the house state fills it (updateHouse).
    const etch = TOOLS.find((t) => t.id === 'etch')
    if (etch) {
      this.wipStand = buildWipStand(this.materials, etch)
      this.wipStand.setCount(1)
      this.group.add(this.wipStand.group)
    }
    if (this.lastHouse) this.updateHouse(...this.lastHouse)

    this.root.add(this.group)
    this.built = true
    // Captured HERE, before the prewarm, and measured twice to be sure. An
    // environment that is assigned but not yet rendered is an incomplete
    // cube texture, and every draw that samples one goes down the driver's
    // slow path: allocating here and capturing after the prewarm turned the
    // two throwaway renders from 0.4 s into 3.9 s and the capture itself
    // into 2.4 s - a 2.2 s boot became 8.3 s. The capture is the page's
    // first render and pays the driver's warm-up; that is the cheaper place
    // for it.
    //
    // And taken under the lamp BUDGET, like every frame. Fresh from
    // buildLighting every lamp is visible, and a capture then compiles the
    // hall's materials against all ninety-odd of them: a family of shaders
    // nothing else ever uses, and the D3D compiler's time explodes with the
    // light count - twenty to thirty seconds cold on the integrated GPU.
    // That was the first boot on any machine, and every fab switch whose
    // giant variants had fallen out of the browser's shader cache. Budgeted
    // around the probe, the capture draws with the programs the prewarm
    // compiles anyway.
    this.updateLights(ENV_PROBE)
    this.refreshEnvironment()
    mark('envmap')
  }

  /**
   * Capture the hall itself as the environment map.
   *
   * Metals are lit almost entirely by what they reflect, and until now they
   * reflected a generated neutral interior - which is why the dark presets had
   * to fake it by dimming the map. Capturing the real scene from mid-aisle
   * fixes both problems at once: steel carries the preset's actual colour, and
   * the polished floor reflects the luminaire rows overhead, which is where a
   * production-bay photograph gets its repeated streaks.
   *
   * One 256 px cube render per preset change, never per frame. The capture is
   * taken with the environment removed so a stale map is not folded into the
   * new one.
   */
  private refreshEnvironment(): void {
    if (!this.renderer || !this.built) return
    if (!this.envTarget) {
      // 512 rather than 256: at 256 the luminaire rows smear into the ceiling
      // glow by the first mip, and the floor has nothing crisp to mirror.
      this.envTarget = new THREE.WebGLCubeRenderTarget(512, {
        generateMipmaps: true,
        minFilter: THREE.LinearMipmapLinearFilter,
      })
    }
    const cam = new THREE.CubeCamera(0.1, 200, this.envTarget)
    // ENV_PROBE, not a literal: the floor's box projection has to undo this
    // exact position to turn a direction back into a place in the room, and
    // the two drifting apart would bend every reflection in the hall.
    cam.position.copy(ENV_PROBE)
    cam.layers.set(ENV_LAYER)
    // Captured from the ROOT, not the group: background and fog live there,
    // and a capture without them would fold a black sky into every metal.
    this.root.environment = null
    // No shadow pass for a reflection. With the key light casting, the
    // capture's first render would also draw the shadow map of the whole
    // hall - every caster uploaded, every depth program compiled - which the
    // prewarm does again moments later under each tier. A ceiling glow in a
    // polished floor does not need a shadow in it.
    const shadows = this.renderer.shadowMap.enabled
    this.renderer.shadowMap.enabled = false
    cam.update(this.renderer, this.root)
    this.renderer.shadowMap.enabled = shadows
    this.root.environment = this.envTarget.texture
  }

  /**
   * The etcher is shown with its lid off - which no running tool ever is, but
   * the whole reason for a simulation is to see the thing you are never allowed
   * to look at.
   */
  private buildEtchCutaway(tool: Tool): void {
    const g = new THREE.Group()
    const facingAisle = aisleDirection(tool)
    g.position.set(tool.position[0] - 2.6, 0, tool.position[2] + facingAisle * 1.4)

    // Pedestal and chuck.
    const pedestal = new THREE.Mesh(
      new THREE.CylinderGeometry(0.62, 0.72, 0.9, 28),
      this.materials.toolPanel,
    )
    pedestal.position.y = 0.45
    pedestal.castShadow = true
    g.add(pedestal)

    const chuck = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.5, 0.08, 32),
      this.materials.steel,
    )
    chuck.position.y = 0.94
    g.add(chuck)

    // The wafer itself, textured from the simulation each frame.
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = 256
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#3c4652'
    ctx.fillRect(0, 0, 256, 256)
    this.waferTexture = new THREE.CanvasTexture(canvas)
    this.waferTexture.colorSpace = THREE.SRGBColorSpace

    this.waferMesh = new THREE.Mesh(
      new THREE.CircleGeometry(0.45, 64),
      new THREE.MeshStandardMaterial({
        map: this.waferTexture,
        roughness: 0.24,
        metalness: 0.45,
      }),
    )
    this.waferMesh.rotation.x = -Math.PI / 2
    this.waferMesh.position.y = 0.99
    g.add(this.waferMesh)

    // Plasma: an additive glow volume above the wafer. Intensity is driven from
    // the solved discharge, so a student can see power and pressure in the room
    // before ever opening a panel.
    this.plasmaGlow = new THREE.Mesh(
      new THREE.CylinderGeometry(0.56, 0.5, 0.5, 32, 1, true),
      new THREE.MeshBasicMaterial({
        color: 0x8fd0ff,
        transparent: true,
        opacity: 0.22,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    )
    this.plasmaGlow.position.y = 1.28
    g.add(this.plasmaGlow)

    const plasmaLight = new THREE.PointLight(0x9fd8ff, 6, 5, 2)
    plasmaLight.position.y = 1.3
    plasmaLight.name = 'plasmaLight'
    g.add(plasmaLight)

    // Quartz bell jar over the whole assembly.
    const dome = new THREE.Mesh(
      new THREE.CylinderGeometry(0.8, 0.8, 1.5, 32, 1, true),
      this.materials.glass,
    )
    dome.position.y = 1.3
    g.add(dome)

    const flange = new THREE.Mesh(
      new THREE.CylinderGeometry(0.86, 0.86, 0.1, 32),
      this.materials.steel,
    )
    flange.position.y = 2.05
    g.add(flange)

    this.group.add(g)
  }

  /** Push a freshly drawn wafer map into the 3D chamber. */
  updateWafer(source: HTMLCanvasElement): void {
    if (!this.waferTexture || !this.waferMesh) return
    const target = this.waferTexture.image as HTMLCanvasElement
    const ctx = target.getContext('2d')!
    ctx.clearRect(0, 0, target.width, target.height)
    ctx.drawImage(source, 0, 0, target.width, target.height)
    this.waferTexture.needsUpdate = true
  }

  /** Drive the plasma glow from the solved discharge. */
  updatePlasma(intensity: number, colour: THREE.ColorRepresentation): void {
    if (!this.plasmaGlow) return
    this.plasmaLevel = Math.min(intensity, 1)
    this.plasmaBase.set(colour)
    const mat = this.plasmaGlow.material as THREE.MeshBasicMaterial
    mat.opacity = 0.06 + 0.42 * this.plasmaLevel
    mat.color.copy(this.plasmaBase)
    const light = this.plasmaGlow.parent?.getObjectByName('plasmaLight') as THREE.PointLight | undefined
    if (light) {
      light.intensity = 1.5 + 12 * this.plasmaLevel
      light.color.copy(this.plasmaBase)
    }
  }

  /**
   * Per-frame motion. Cheap on purpose - a handful of rotations and one
   * colour write - because it runs every frame on every tier.
   *
   * The plasma flickers around the level the solver set: a discharge is a
   * live thing, and a glow that sits perfectly still reads as a painted
   * tube. The flicker is two incommensurate sines, so it never visibly
   * repeats, and it scales with the discharge so a cold chamber is still.
   */
  animate(dt: number, eye: THREE.Vector3): void {
    this.clock += dt
    for (const part of this.spinners) {
      part.rotation.y += (part.userData.spin as number) * dt
    }
    this.oht.advance(dt)
    if (this.subfab && this.subfab.group.visible) {
      this.subfab.animate(dt, eye)
      this.crew?.advance(dt, eye)
    }
    this.amrs.advance(dt, eye)
    this.stockers.advance(dt)
    this.attachmentsGroup.animate(this.clock)
    this.bays.animate(dt, eye)
    this.chase.update(eye)
    if (this.chase.group.visible) this.chaseCrew?.advance(dt, eye)
    this.people?.advance(dt, eye)
    if (this.plasmaGlow && this.plasmaLevel > 0) {
      const t = this.clock
      const flicker = 1 + this.plasmaLevel * (0.08 * Math.sin(t * 23) + 0.05 * Math.sin(t * 7.3))
      const mat = this.plasmaGlow.material as THREE.MeshBasicMaterial
      // Pushed past 1.0 as the discharge climbs, so a hot plasma is the one
      // thing in a chamber cutaway that blooms.
      mat.color.copy(this.plasmaBase).multiplyScalar((1 + 1.4 * this.plasmaLevel) * flicker)
    }
  }

  /**
   * Keep the key light's shadow box around the student.
   *
   * The directional light shadows a fixed 52 m box, which on a 136 m hall
   * meant a tool at either end stood on a floor with no shadow under it -
   * the single biggest reason the far bays read as models on a table. The
   * box now travels with the eye, so every tool the student can see has its
   * contact shadow. The box is snapped to the shadow map's own texel grid
   * along its two axes, which is what stops the shadow edges shimmering as
   * the student walks: a box that moves by fractions of a texel re-samples
   * every edge every frame.
   */
  private readonly shadowOffset = KEY_OFFSET.clone()
  /** The light's orientation - constant, since the offset is. */
  private readonly shadowFrame = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().lookAt(this.shadowOffset, new THREE.Vector3(), new THREE.Vector3(0, 1, 0)),
  )
  private readonly shadowFrameInv = this.shadowFrame.clone().invert()
  private readonly shadowScratch = new THREE.Vector3()

  /**
   * Widen the shadow box to the whole hall, or put it back. For the prewarm
   * render only: a shadow map is drawn with depth materials that
   * `renderer.compile` never sees, so their programs compile the first time
   * a caster enters the box - which, with the box following the eye, would
   * be the first tool a walking student reached. One render with every
   * caster in the box compiles them all behind the splash.
   */
  setShadowScope(scope: 'hall' | 'eye'): void {
    const cam = this.rig.key.shadow.camera
    const s = scope === 'hall' ? FAB.length / 2 + 10 : SHADOW_HALF_EXTENT
    cam.left = -s
    cam.right = s
    cam.top = s
    cam.bottom = -s
    cam.far = scope === 'hall' ? 200 : SHADOW_FAR
    cam.updateProjectionMatrix()
  }

  /**
   * How much of the SUB-FAB's light regime is in force: 0 on the fab floor,
   * 1 on the slab below, and the stair is the crossfade.
   *
   * WHY THE ROOM NEEDS ITS OWN REGIME. The hall's fills - a hemisphere, a
   * directional key and the environment capture - are lighting for a room
   * with a luminous ceiling and daylight-white walls. Applied to a concrete
   * box under the floor they arrive from everywhere at once, which is the
   * light that destroys form (the same argument the environment note above
   * makes): the strips stopped reading as sources, the coffered slab lost
   * its depth, and the room came out evenly lit end to end, which is the
   * one thing a sub-fab never is. Down there the strips are ALL there is,
   * so the fills fade out and the strips take up the slack - which is not a
   * mood, it is what the room's own drawing says: no FFU ceiling, no
   * windows, eleven battens.
   *
   * IT COSTS NOTHING PER FRAME AND CHANGES NO LIGHT COUNT. Intensities
   * only - the same lamps, in the same per-tier budget that fixed the
   * walking hitch. Nothing here can make the renderer recompile a shader.
   */
  private subfabMix = 0
  /**
   * The chase's own regime, keyed the same way: 0 in the hall, 1 once the
   * eye is a stride past the bulkhead. It fades across the door reveal,
   * the one place the two rooms are seen one at a time. Milder than the
   * sub-fab's - a chase has a deck and no FFU ceiling, but it is not a
   * basement - and its strips take over from the fills as they go.
   */
  private chaseMix = 0

  /**
   * Scale the fills and the sub-fab strips for the current mix. Called on
   * every eye move and after any preset change, since applyLighting sets
   * these absolutely and would otherwise put the hall's fills back while
   * the student is standing under the slab.
   */
  private applyLightMix(): void {
    const m = this.subfabMix
    const c = this.chaseMix
    const p = lightingPreset()
    this.rig.hemi.intensity = p.hemiIntensity * (1 - 0.72 * m) * (1 - 0.5 * c)
    this.rig.key.intensity = p.keyIntensity * (1 - 0.88 * m) * (1 - 0.7 * c)
    this.root.environmentIntensity = p.envIntensity * (1 - 0.6 * m) * (1 - 0.4 * c)
    for (const lamp of this.lamps) {
      const dim = (lamp.userData.dim as number | undefined) ?? 1
      if (lamp.userData.subfab === true) lamp.intensity = p.lampIntensity * dim * (1 + 0.85 * m)
      // Off entirely in the hall: a point light does not stop at the
      // bulkhead, and at any strength from the aisle these lit the tool
      // faces from behind. They cost the same to the budget dark or lit.
      else if (lamp.userData.chase === true) lamp.intensity = p.lampIntensity * dim * c
    }
  }

  followEye(eye: THREE.Vector3): void {
    // The light regime crosses over on the stair, which is enclosed: the
    // fade happens where a student can see one room at a time. Keyed to the
    // eye rather than to a timer so walking back up undoes it exactly.
    const mix = Math.min(1, Math.max(0, (0.9 - eye.y) / 1.7))
    // The chase crosses over in the doorway, over 0.7 m from just inside
    // the wall's own collider - which is only reachable through a door.
    // A ballroom has no doorway and no other room to cross into: one
    // regime, wall to wall.
    const chase =
      !BALLROOM && eye.y > -0.5 ? Math.min(1, Math.max(0, (Math.abs(eye.z) - (BULKHEAD_Z - 0.35)) / 0.7)) : 0
    if (Math.abs(mix - this.subfabMix) > 0.002 || Math.abs(chase - this.chaseMix) > 0.002) {
      this.subfabMix = mix
      this.chaseMix = chase
      this.applyLightMix()
    }

    // The sub-fab is under the floor, and from the hall it is invisible
    // everywhere but through the stairwell hole - yet every skid and pipe
    // down there is inside the frustum and gets drawn behind the floor,
    // a couple of hundred draw calls for nothing. It is drawn only when
    // the student is below the floor or close enough to the stair to see
    // down it.
    const stair = SUBFAB.stair
    const dx = eye.x - (stair.minX + stair.maxX) / 2
    const dz = eye.z - (stair.minZ + stair.maxZ) / 2
    if (this.subfab) this.subfab.group.visible = eye.y < 1.0 || dx * dx + dz * dz < 30 * 30

    const key = this.rig.key
    const cam = key.shadow.camera
    const texel = (cam.right - cam.left) / key.shadow.mapSize.x
    // Snap in the light's own frame, not the world's: the light looks along a
    // diagonal, so a world-axis snap would still slide across the map.
    const p = this.shadowScratch.set(eye.x, 0, eye.z).applyQuaternion(this.shadowFrameInv)
    p.x = Math.round(p.x / texel) * texel
    p.y = Math.round(p.y / texel) * texel
    p.applyQuaternion(this.shadowFrame)
    key.target.position.copy(p)
    key.position.copy(p).add(this.shadowOffset)
  }

  /** Where a tool's screen sits in the world - the anchor for the Open button. */
  hmiScreenWorld(toolId: string, out: THREE.Vector3): THREE.Vector3 | null {
    const screen = this.hmiScreens.get(toolId)
    if (!screen) return null
    return screen.getWorldPosition(out)
  }

  /**
   * Flag a tool's screen dirty after HmiScreens repainted its canvas. The
   * texture was bound to that same canvas at construction, so there is
   * nothing to hand over - and nothing here that can change a program hash.
   */
  updateHmi(toolId: string, readout?: HmiReadout, processName = ''): void {
    const screen = this.hmiScreens.get(toolId)
    const mat = screen?.material as THREE.MeshBasicMaterial | undefined
    if (mat?.map) mat.map.needsUpdate = true
    // The tower and the bay's andon say the same thing the screen does.
    // The tool down for maintenance holds red until its panel is actually
    // run - a technician inside it and a lot going through are not both.
    if (!readout) return
    const tool = TOOLS.find((t) => t.id === toolId)
    if (!tool) return
    const down = toolId === MAINTENANCE_TOOL && readout.state === 'idle'
    const state: TowerState = down ? 'down' : readout.state
    // A tool newly in alarm is what the floor hears (see ambience).
    if (state === 'alarm' && this.towerStates.get(toolId) !== 'alarm') {
      this.lastAlarm = { x: tool.position[0], z: toolFaceZ(tool), at: this.clock }
    }
    this.towerStates.set(toolId, state)
    this.attachmentsGroup.setTower(toolId, state)
    this.bays.update(tool, readout, processName)
  }

  private readonly towerStates = new Map<string, TowerState>()
  private lastAlarm: { x: number; z: number; at: number } | null = null

  /** What the student can hear from where they stand (sound.ts). */
  private readonly soundScratch = new THREE.Vector3()

  ambience(eye: THREE.Vector3): SoundSources {
    let hoistD2 = Infinity
    for (const v of this.oht.vehicles) {
      if (v.state !== 'cruise' || v.holding) continue
      hoistD2 = Math.min(hoistD2, v.group.position.distanceToSquared(eye))
    }
    let robotD2 = Infinity
    for (const r of this.amrs.robots) {
      if (r.state !== 'move' && r.state !== 'cross') continue
      robotD2 = Math.min(robotD2, r.group.position.distanceToSquared(eye))
    }
    const a = this.lastAlarm
    const alarmD2 = a ? this.soundScratch.set(a.x, 1.5, a.z).distanceToSquared(eye) : Infinity
    const alarmAge = a ? this.clock - a.at : Infinity
    return { hoistD2, robotD2, alarmD2, alarmAge }
  }

  /**
   * What is under the cursor, within reach?
   *
   * `range` widens the default arm's-length cap. Touch navigation passes
   * Infinity: a tap on a tool across the bay is a request to walk there, so the
   * pick has to be able to say which tool that was.
   */
  pickPointer(
    ndc: THREE.Vector2,
    camera: THREE.Camera,
    range = INTERACT_RANGE,
    forgiving = false,
  ): PickResult | null {
    this.raycaster.setFromCamera(ndc, camera)
    return this.resolve(range, forgiving)
  }

  /** What is straight ahead, within reach? Kept for the keyboard shortcut. */
  pick(origin: THREE.Vector3, direction: THREE.Vector3, range = INTERACT_RANGE): PickResult | null {
    this.ray.set(origin, direction)
    this.raycaster.set(origin, direction)
    return this.resolve(range)
  }

  /**
   * `forgiving` admits the invisible trigger volumes as a fallback. Off by
   * default: a volume reaches nearly a metre past the machine into the
   * aisle, so a click on the floor beside a tool - or a cursor merely
   * passing that floor - counted as the tool, and a selection that can
   * happen without touching the machine is not a selection. Only the
   * phone's far tap asks for it, where a fingertip on a tool thirty metres
   * away covers more air than machine.
   */
  private resolve(range = INTERACT_RANGE, forgiving = false): PickResult | null {
    this.raycaster.far = range

    /*
     * MACHINE GEOMETRY IS GROUND TRUTH. The crosshair resting on a mesh of
     * tool X means the student is looking at X, and X wins - full stop. The
     * previous design asked the trigger volumes first and only consulted
     * geometry as a veto, and it had a hole: the volumes of neighbouring
     * tools overlap (a four-chamber cluster casts a wide box), so standing
     * at THRM-04's screen the distance-sorted trigger hits could open with
     * the NEIGHBOUR's box face, and the neighbour was accepted while the
     * crosshair sat squarely on THRM-04's HMI. Asking the geometry first,
     * nearest mesh across every tool, makes that whole class of overlap
     * bug impossible.
     */
    let best: PickResult | null = null
    for (const [id, group] of this.toolGroups) {
      // Only what gets DRAWN gets its world matrices refreshed, and this
      // must answer correctly even on a frame nothing has rendered yet.
      group.updateWorldMatrix(true, true)
      const hit = this.raycaster.intersectObject(group, true)[0]
      if (hit && (!best || hit.distance < best.distance)) {
        const tool = TOOLS.find((s) => s.id === id)
        if (tool) best = { tool, distance: hit.distance }
      }
    }
    if (best || !forgiving) return best

    /*
     * FALLBACK: the invisible trigger volumes, for aims that graze a tool
     * without touching a mesh - the gap between chambers, the air over a
     * loadport. A volume the eye is INSIDE is the room around you, and with
     * the geometry pass above already handling "looking at the machine
     * beside you", an enclosing volume only matches when the student is
     * looking at nothing of the tool - so it must pass, or the gowning bay
     * would eclipse every travel tap from spawn.
     */
    for (const trigger of this.triggers) trigger.updateWorldMatrix(true, false)
    const hits = this.raycaster.intersectObjects(this.triggers, false)
    for (const hit of hits) {
      const id = hit.object.userData.toolId as string
      const tool = TOOLS.find((s) => s.id === id)
      if (!tool) return null
      if (this.eyeInside(hit.object)) continue
      return { tool, distance: hit.distance }
    }
    return null
  }

  private readonly eyeBox = new THREE.Box3()

  private eyeInside(object: THREE.Object3D): boolean {
    const mesh = object as THREE.Mesh
    if (!mesh.geometry) return false
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox()
    this.eyeBox.copy(mesh.geometry.boundingBox!).applyMatrix4(mesh.matrixWorld)
    return this.eyeBox.containsPoint(this.raycaster.ray.origin)
  }

  /**
   * Keep only the downlights that can actually reach the student.
   *
   * Each lamp has a 15 m falloff, so anything much further than that
   * contributes nothing to the image but is still evaluated per fragment.
   * Called from the same throttled block that pushes solver state into the
   * scene, which is often enough - the lamps are 10 m apart and a walking
   * student covers that in three seconds.
   */
  /**
   * Repaint the whole rig from the active lighting preset. Called once at
   * construction and again whenever the student picks a different mood from
   * the main menu - it touches only colours and intensities, so a change costs
   * nothing and never disturbs the solver or the geometry.
   */
  applyLighting(): void {
    const p = lightingPreset()

    this.root.background = new THREE.Color(p.background)
    this.root.fog!.color.set(p.fog)
    this.root.environmentIntensity = p.envIntensity

    this.rig.hemi.color.set(p.hemiSky)
    this.rig.hemi.groundColor.set(p.hemiGround)
    this.rig.hemi.intensity = p.hemiIntensity
    this.rig.key.color.set(p.key)
    this.rig.key.intensity = p.keyIntensity

    for (const lamp of this.lamps) {
      // The litho bay's amber is process physics, not mood - resist is blind
      // to it - so it keeps its own colour under every sky.
      const litho = lamp.userData.litho === true
      lamp.color.set(litho ? LITHO_LIGHT_COLOUR : p.lamp)
      // The aisle downlights carry a per-lamp dim factor: same colour as the
      // bay lamps, a fraction of the punch, because there are five of them in
      // view for every one bay lamp.
      const dim = (lamp.userData.dim as number | undefined) ?? 1
      lamp.intensity = litho ? 18 : p.lampIntensity * dim
    }

    this.materials.ffu.emissive.set(p.ffuEmissive)
    this.materials.ffu.emissiveIntensity = p.ffuIntensity
    this.materials.floor.color.set(p.floor)
    // Pushed past 1.0 on purpose: an unlit material caps at its colour, and
    // at 1.0 a diffuser panel reads dimmer than the specular pool under a
    // downlight. Overdriving the colour lets ACES tone mapping compress it
    // the way a camera compresses a real luminaire - brighter and whiter at
    // the centre - without touching any light. The mirror gets a smaller
    // push; its brightness knob is the material's opacity.
    this.materials.lightPanel.color.set(p.panel).multiplyScalar(2.6)

    // A preset writes the fills absolutely, so the sub-fab's regime has to
    // be laid back over the top of it.
    this.applyLightMix()

    // The environment is a capture of this scene, so it has to be retaken
    // after the repaint - otherwise every reflection is of the previous mood.
    this.refreshEnvironment()
  }

  /** Which quality tier's lamps participate. Set by the governor via main. */
  private qualityTier: QualityTier = 'low'

  setQualityTier(tier: QualityTier): void {
    this.qualityTier = tier
  }

  /**
   * How many lamps each tier keeps visible - a CONSTANT per tier, and that
   * constancy is the entire point.
   *
   * The forward renderer bakes the visible light count into every shader
   * program: change the count and three.js compiles a fresh program set for
   * everything on screen, synchronously, mid-frame. The first version of
   * `updateLights` culled by radius, so the count changed every few steps and
   * walking the aisle froze at each count the session had not met yet - the
   * "navigation gets stuck" bug, and it survived every fix aimed at the
   * movement code because it was never in the movement code.
   *
   * The budget is the worst case the old radius rule would have lit, measured
   * over the walkable floor at construction, so no spot in the hall is darker
   * than it was - the change is that sparse spots now carry a few far lamps
   * that contribute nothing visible. Their GPU cost is exactly the budget the
   * radius rule was already paying at its densest spot.
   */
  private readonly lightBudget = { low: 0, high: 0 }

  private computeLightBudgets(): void {
    const halfW = FAB.bayDepth + FAB.aisleHalfWidth
    // Distances are 3D since the sub-fab: two walkable levels 3 m apart mean
    // an XZ-only rule would count a lamp through the floor as "near".
    const countAt = (x: number, eyeY: number, z: number, high: boolean): number => {
      let n = 0
      for (const lamp of this.lamps) {
        if (lamp.userData.quality === 'high' && !high) continue
        const dx = lamp.position.x - x
        const dy = lamp.position.y - eyeY
        const dz = lamp.position.z - z
        const r = (lamp.userData.cullRadius as number | undefined) ?? LIGHT_CULL_RADIUS
        if (dx * dx + dy * dy + dz * dz < r * r) n++
      }
      return n
    }
    for (const tier of ['low', 'high'] as const) {
      let worst = 0
      // 1.5 m steps against lamps 10+ m apart: finer sampling cannot find a
      // denser spot, and the whole scan is a few hundred thousand compares,
      // once, at build. Both walkable levels are scanned - the budget means
      // "every lamp that could touch the image", wherever the student stands.
      for (let x = -FAB.length / 2; x <= FAB.length / 2; x += 1.5) {
        for (let z = -halfW; z <= halfW; z += 1.5) {
          worst = Math.max(worst, countAt(x, 1.68, z, tier === 'high'))
        }
      }
      for (let x = SUBFAB.minX; x <= SUBFAB.maxX; x += 1.5) {
        for (let z = SUBFAB.minZ; z <= SUBFAB.maxZ; z += 1.5) {
          worst = Math.max(worst, countAt(x, 1.68 - SUBFAB.depth, z, tier === 'high'))
        }
      }
      this.lightBudget[tier] = worst
    }
  }

  /** Scratch for the nearest-K selection, kept to avoid a per-tick allocation. */
  private readonly lampsByDistance: THREE.PointLight[] = []

  updateLights(eye: THREE.Vector3): void {
    const high = this.qualityTier === 'high'
    const budget = high ? this.lightBudget.high : this.lightBudget.low

    this.lampsByDistance.length = 0
    for (const lamp of this.lamps) {
      // Lamps above the active quality tier stay dark - an invisible light
      // costs the GPU nothing, so the whole high-tier set can exist and a
      // tier change is just this flag flipping.
      if (lamp.userData.quality === 'high' && !high) {
        lamp.visible = false
        continue
      }
      // 3D, not XZ: a sub-fab lamp directly underfoot is far from the eye in
      // the only sense that matters, and must not steal a ceiling lamp's slot
      // through the floor - nor the other way around.
      const dx = lamp.position.x - eye.x
      const dy = lamp.position.y - eye.y
      const dz = lamp.position.z - eye.z
      lamp.userData.d2 = dx * dx + dy * dy + dz * dz
      // A DARK LAMP NEVER TAKES A SLOT FROM A LIT ONE. The chase's strips
      // are at intensity 0 from the aisle (see applyLightMix) and 4.4 m
      // away through the wall - nearer than most of the aisle's own lamps,
      // so by distance alone they would have filled the aisle's budget
      // with lights that contribute nothing and pushed real ones out.
      // Sorted to the back, they take only the slots nobody lit wanted.
      if (lamp.intensity <= 0) lamp.userData.d2 += 1e6
      this.lampsByDistance.push(lamp)
    }

    // Exactly `budget` lamps visible, always the nearest ones. The COUNT never
    // changes while walking, so the shader programs compiled on the first
    // frame serve the whole session - see `lightBudget` for why that matters.
    this.lampsByDistance.sort(
      (a, b) => (a.userData.d2 as number) - (b.userData.d2 as number),
    )
    for (let i = 0; i < this.lampsByDistance.length; i++) {
      this.lampsByDistance[i].visible = i < budget
    }
  }

  /**
   * Compile the shader programs for BOTH tiers' light counts, up front.
   *
   * With the count constant per tier there are only two lighting states a
   * session can ever be in, so both can be paid for at build time - behind
   * the splash - instead of the high-tier set compiling mid-walk when the
   * governor promotes a machine three seconds into the session.
   */
  /**
   * The prewarm for a sub-fab built after the boot - its objects only,
   * against the whole scene's lights, under each tier.
   *
   * The GLB arrivals re-run the full prewarm and pay for it: six compiles
   * and two renders over every mesh in the hall, seconds on the integrated
   * GPU. The room's own materials are a couple of dozen programs at most,
   * and `renderer.compile` takes the objects to visit and the scene to take
   * lights from as separate arguments, so this visits the room and reads
   * the lights from the root. The image pipeline's variants and the depth
   * programs are shared with the hall's meshes and already compiled.
   */
  prewarmSubfab(camera: THREE.Camera, eye: THREE.Vector3): void {
    if (!this.renderer || !this.subfab) return
    const original = this.qualityTier
    for (const tier of ['high', 'low'] as const) {
      this.qualityTier = tier
      this.updateLights(eye)
      this.renderer.compile(this.subfab.group, camera, this.root)
    }
    this.qualityTier = original
    this.updateLights(eye)
  }

  prewarmLighting(camera: THREE.Camera, eye: THREE.Vector3, alsoCompile?: () => void): void {
    if (!this.renderer) return
    const original = this.qualityTier
    for (const tier of ['high', 'low'] as const) {
      this.qualityTier = tier
      this.updateLights(eye)
      this.renderer.compile(this.root, camera)
      if (BOOT_TIMING) performance.mark('boot:pre:' + tier + ' scene compile')
      // Whatever else has program variants that bake in the light count -
      // the image pipeline's off-screen set - compiles under each tier too.
      alsoCompile?.()
    }
    this.qualityTier = original
    this.updateLights(eye)
  }

  /** Axis-aligned boxes the player cannot walk through. */
  colliders(): Collider[] {
    // Everything on THIS floor is confined to this floor's heights, so none
    // of it blocks the student walking the sub-fab slab beneath it.
    const fabLevel = { minY: -0.5, maxY: FAB.ceilingHeight }
    const out: Collider[] = []
    for (const st of TOOLS) {
      const [x, , z] = st.position
      // Derived from the same footprint the mesh is built from, plus a little
      // clearance, so a wide tool cannot be walked through just because the
      // collider forgot it was wide. A bulkheaded face is boxed from its
      // load ports back to the wall, its side cabinets included; the
      // free-standing tools on the axis are boxed whole.
      const size = toolSize(st)
      if (z === 0) {
        // The gowning station is an open island - a bench and a rail down
        // its middle (buildTool) - so it is boxed to those, not to the
        // footprint a cabinet would have.
        const halfW = size.w / 2 + 0.5
        const halfD = st.kind === 'gowning' ? 0.75 : size.d / 2 + 0.4
        out.push({ minX: x - halfW, maxX: x + halfW, minZ: z - halfD, maxZ: z + halfD, ...fabLevel })
        if (st.kind === 'gowning') {
          // Its HMI pedestal stands off the island's front, at the face's
          // local +x, which the half-turn toward the aisle puts at world -x.
          const px = x - (size.w / 2 - 0.4)
          const pz = z + size.d / 2 + 0.62
          out.push({ minX: px - 0.3, maxX: px + 0.3, minZ: pz - 0.3, maxZ: pz + 0.3, ...fabLevel })
        }
        continue
      }
      const reach = SIDE_REACH[st.kind] ?? { minus: 0.3, plus: 0.3 }
      const facing = aisleDirection(st)
      const minX = x - size.w / 2 - (facing > 0 ? reach.plus : reach.minus)
      const maxX = x + size.w / 2 + (facing > 0 ? reach.minus : reach.plus)
      const face = toolFaceZ(st)
      const near = face + facing * 0.75
      // Through the wall to the mainframe's back in the chase, since the
      // chase is walked.
      const depth = Math.max(size.d, 1.55)
      const back = face - facing * (depth + 0.2)
      out.push({ minX, maxX, minZ: Math.min(near, back), maxZ: Math.max(near, back), ...fabLevel })
      // Fab B's copy of this tool, turned about the service aisle's
      // centreline: the same box, mirrored in z about the pivot.
      if (BALLROOM) {
        const pz = 2 * Math.sign(z) * SECOND_ROW_PIVOT_Z
        out.push({ minX, maxX, minZ: Math.min(pz - near, pz - back), maxZ: Math.max(pz - near, pz - back), ...fabLevel })
      }

      // The pump and the gas box, beside the mainframe in the chase.
      //
      // These used to be covered by widening the WHOLE tool box to 0.95 m
      // past the body - from 0.75 m in front of the face all the way to the
      // mainframe's back. That is a wall of collision either side of every
      // tool, and it stood across the strip of chase floor the bay's own
      // CHASE ACCESS door opens onto: the door is placed 0.75 m past the
      // face's side furniture, which on a tool with little of it lands
      // inside that widening. Nine of the ten doors on the -z row and six
      // of the ten on +z could be opened, and seen through, and not walked
      // through. Two boxes where the two boxes actually are is both smaller
      // and true, and it lets the wall's own doorways do the gating.
      //
      // Both are drawn in the tool's local frame, which the facing yaw
      // mirrors in x; their z is measured from the bulkhead, which is where
      // `buildTool` puts the bulk of every bay tool whatever its depth.
      const side = Math.sign(st.position[2])
      const mirror = facing > 0 ? -1 : 1
      const chaseBox = (lx: number, w: number, lz: number, d: number): void => {
        const xs = [x + mirror * (lx - w / 2), x + mirror * (lx + w / 2)]
        const zs = [side * (BULKHEAD_Z + lz - d / 2), side * (BULKHEAD_Z + lz + d / 2)]
        out.push({
          minX: Math.min(...xs), maxX: Math.max(...xs),
          minZ: Math.min(...zs), maxZ: Math.max(...zs),
          ...fabLevel,
        })
      }
      // No pump box in a ballroom (`buildTool`); the gas box stands in both -
      // and in Fab B behind the copy too, turned with it (its local x flips).
      if (!BALLROOM) chaseBox(size.w / 2 + 0.55, 0.8, 1.3, 0.8)
      chaseBox(-(size.w / 2 + 0.5), 0.7, 1.1, 0.6)
      if (BALLROOM) {
        const d = 1.1
        const xs = [x - mirror * (-(size.w / 2 + 0.5) - 0.35), x - mirror * (-(size.w / 2 + 0.5) + 0.35)]
        const zc = side * (2 * SECOND_ROW_PIVOT_Z - BULKHEAD_Z - d)
        out.push({ minX: Math.min(...xs), maxX: Math.max(...xs), minZ: Math.min(zc - 0.3, zc + 0.3), maxZ: Math.max(zc - 0.3, zc + 0.3), ...fabLevel })
      }
    }
    // The bulkhead (with its doorways), the chase furniture, and the rest.
    for (const c of this.bays.colliders()) out.push({ ...c, ...fabLevel })
    for (const c of this.chase.colliders()) out.push({ ...c, ...fabLevel })
    // The etch cutaway pedestal - on a floor that has an etcher on it, which
    // a floor somebody built themselves need not (fab/floorPlan.ts).
    const etch = TOOLS.find((s) => s.id === 'etch')
    if (etch) {
      out.push({
        minX: etch.position[0] - 3.5,
        maxX: etch.position[0] - 1.7,
        minZ: etch.position[2] - 2.3,
        maxZ: etch.position[2] - 0.5,
        ...fabLevel,
      })
    }
    // Gowning lockers and bench.
    out.push({
      minX: GOWNING.position[0] - 2.3,
      maxX: GOWNING.position[0] + 2.3,
      minZ: GOWNING.position[2] - 3.0,
      maxZ: GOWNING.position[2] - 1.2,
      ...fabLevel,
    })
    // The lower level: skids, columns, and the stairwell's cheeks and rails.
    // Bay partitions and stocker racks.
    for (const p of partitions()) out.push({ ...partitionCollider(p), ...fabLevel })
    for (const c of stockerColliders()) out.push({ ...c, ...fabLevel })
    for (const c of this.people?.colliders() ?? []) out.push({ ...c, ...fabLevel })
    for (const c of this.chaseCrew?.colliders() ?? []) out.push({ ...c, ...fabLevel })
    out.push(...subfabColliders())
    const slabLevel = { minY: -SUBFAB.depth - 0.5, maxY: SUBFAB.ceilingY }
    for (const c of this.crew?.colliders() ?? []) out.push({ ...c, ...slabLevel })
    return out
  }

  /** Repaint the sub-fab's beacons and utility board from the live house. */
  updateSubfab(house: HouseState, armedId: string | null): void {
    this.lastHouse = [house, armedId]
    this.subfab?.update(house, armedId)
    this.updateHouse(house, armedId)
  }

  /**
   * What the FLOOR and the CHASE show of the house state - the sub-fab has
   * its own beacons (updateSubfab). Warm process cooling water is the one
   * house fault with a picture up here: the etcher's WIP stand fills, and
   * the beacon on the PCW header behind it goes red. The first time the
   * water runs warm the floor hears it from the chase, the way it hears a
   * tool go into alarm.
   */
  updateHouse(house: HouseState, _armedId: string | null): void {
    const warm = house.pcwOffsetC > 5
    // Applied every time, not only on change: a rebuilt floor (a fab
    // switch) replays the last house state onto new objects.
    this.wipStand?.setCount(warm ? 6 : 1)
    this.chase?.setPcwWarm(warm)
    const newlyWarm = warm && !this.pcwWarm
    this.pcwWarm = warm
    const etch = TOOLS.find((t) => t.id === 'etch')
    if (newlyWarm && etch) this.lastAlarm = { x: etch.position[0], z: etch.position[2] - Math.sign(etch.position[2]) * 2.5, at: this.clock }
  }

  /**
   * Which hoist vehicle or floor robot the pointer is on, within reach.
   * Its own pick, like the skids: a vehicle is not a tool, and nothing that
   * expects a Tool may ever be handed one. Geometry only - each vehicle is
   * a few merged meshes with tight bounds, so this is cheap enough to run
   * for the cursor every frame.
   */
  pickVehicle(ndc: THREE.Vector2, camera: THREE.Camera, range = INTERACT_RANGE): VehicleRef | null {
    this.raycaster.setFromCamera(ndc, camera)
    this.raycaster.far = range
    const groups = [...this.oht.vehicles.map((v) => v.group), ...this.amrs.robots.map((r) => r.group)]
    for (const g of groups) g.updateWorldMatrix(true, true)
    const hit = this.raycaster.intersectObjects(groups, true)[0]
    if (!hit) return null
    let o: THREE.Object3D | null = hit.object
    while (o && !o.userData.vehicleRef) o = o.parent
    return (o?.userData.vehicleRef as VehicleRef | undefined) ?? null
  }

  /**
   * Which technician the pointer is on, within reach: their cast id, or
   * null. The figures are skinned meshes plus the gowning parented to their
   * bones; the pick walks up from whichever part the ray met.
   */
  pickPerson(ndc: THREE.Vector2, camera: THREE.Camera, range = INTERACT_RANGE): string | null {
    this.raycaster.setFromCamera(ndc, camera)
    this.raycaster.far = range
    if (this.people) {
      this.people.group.updateWorldMatrix(true, true)
      const hit = this.raycaster.intersectObject(this.people.group, true)[0]
      if (hit) return this.people.personOf(hit.object)
    }
    // The chase crew, only while the chase is drawn: from the aisle they
    // are behind a wall the ray does not stop at.
    if (this.chaseCrew && this.chase.group.visible) {
      this.chaseCrew.group.updateWorldMatrix(true, true)
      const hit = this.raycaster.intersectObject(this.chaseCrew.group, true)[0]
      if (hit) return this.chaseCrew.personOf(hit.object)
    }
    return null
  }

  /** Whichever crew this cast member belongs to. */
  private crewOf(id: string): People | ChaseCrew | null {
    if (this.chaseCrew?.has(id)) return this.chaseCrew
    return this.people
  }

  /** Turn a technician to the student for a conversation (people.ts, chaseCrew.ts). */
  engagePerson(id: string): void {
    // One conversation at a time across both crews.
    this.people?.disengage()
    this.chaseCrew?.disengage()
    this.crewOf(id)?.engage(id)
  }

  disengagePerson(): void {
    this.people?.disengage()
    this.chaseCrew?.disengage()
  }

  /** Whether the engaged technician is speaking or listening. */
  setPersonTalking(on: boolean): void {
    this.people?.setTalking(on)
    this.chaseCrew?.setTalking(on)
  }

  /** Where a technician stands, for reach checks. Null if not loaded. */
  personPosition(id: string, out: THREE.Vector3): THREE.Vector3 | null {
    const p = this.crewOf(id)?.people.find((q) => q.id === id)
    return p ? out.copy(p.group.position) : null
  }

  /** Where someone is on the aisle and which way they were walking. */
  personWhere(id: string): { x: number; z: number; dir: number } | null {
    return this.crewOf(id)?.whereIs(id) ?? null
  }

  /** The live facts behind a vehicle card. */
  vehicleStatus(ref: VehicleRef): VehicleStatus {
    return ref.kind === 'oht' ? this.oht.status(ref.vehicle) : this.amrs.status(ref.robot)
  }

  /** Where a vehicle is right now, for the card's reach check. */
  vehiclePosition(ref: VehicleRef, out: THREE.Vector3): THREE.Vector3 {
    const g = ref.kind === 'oht' ? ref.vehicle.group : ref.robot.group
    return out.copy(g.position)
  }

  /**
   * Which plant skid the pointer is on, within working reach. Its own raycast
   * rather than a shared trigger list: a skid is not a station, and the tool
   * pick must never return one to code that expects a Tool.
   */
  pickSkid(ndc: THREE.Vector2, camera: THREE.Camera, range = 5.5): HouseSystem | 'mau' | null {
    this.raycaster.setFromCamera(ndc, camera)
    this.raycaster.far = range
    if (!this.subfab) return null
    for (const t of this.subfab.triggers) t.updateMatrixWorld()
    const hit = this.raycaster.intersectObjects(this.subfab.triggers, false)[0]
    return hit ? ((hit.object.userData.skidSystem as HouseSystem | 'mau') ?? null) : null
  }

  /**
   * Where the camera should sit to read a tool's HMI.
   *
   * AIMED AT THE SCREEN, which it was not. This used to look at a fixed
   * 1.3 m along world x from the tool's position, written when every face
   * carried its screen at about that offset. The screen now hangs off the
   * EFEM's outboard corner (`hmiFaceX`), which is anywhere from 1.1 m one
   * side of centre to 2.1 m the other, so on eleven tools the glide ended
   * half a metre from a blank stretch of skin with the screen out of frame.
   *
   * The eye stands a little to one side of the screen's axis - the side the
   * posted technician does NOT stand on (people.ts steps them +0.2 m) - so
   * whoever is at the tool is beside the screen in the picture rather than
   * in front of it.
   */
  hmiViewpoint(tool: Tool): { eye: THREE.Vector3; target: THREE.Vector3 } {
    const facingAisle = aisleDirection(tool)
    const lx = hmiFaceX(tool)
    // The face's local x runs against world x on the -z row (bays.ts).
    const screenX = tool.position[0] + (facingAisle > 0 ? -lx : lx)
    // The bezel hangs 0.62 m proud of the face, on its arm.
    const screenZ = toolFaceZ(tool) + facingAisle * 0.62
    // A metre off the glass, which is CLOSER to the face than the technician
    // stands (1.5 m): at 1.25 m the eye was behind them and their head took a
    // third of the picture.
    const eye = new THREE.Vector3(screenX - 0.5, 1.62, screenZ + facingAisle * 1.0)
    const target = new THREE.Vector3(screenX, 1.5, screenZ)
    return { eye, target }
  }

  /**
   * How far off a tool face the student is put down: close enough to read
   * the HMI without walking, far enough back to see the whole face and the
   * light tower over it, and inside the walkway line so they are not stood
   * in the robot lane.
   */
  private static readonly STANDOFF = 1.45

  /** Standing position in front of a tool, used by the assistant's "take me there". */
  approachPoint(tool: Tool): THREE.Vector3 {
    const facingAisle = aisleDirection(tool)
    // Measured from the FACE, not from the aisle's half width. The two used
    // to give the same answer and no longer do: the aisle is 2.7 m narrower
    // than the tool row is deep, so half the aisle width put the student
    // inside the tool they had asked to be taken to.
    return new THREE.Vector3(
      tool.position[0],
      1.68,
      toolFaceZ(tool) + facingAisle * FabScene.STANDOFF,
    )
  }
}
